<template>
  <PopupInputgroup
    ref="popup"
    class="money-filter"
    :label="placeholder"
    :append-to="appendTo"
    :disabled="!!disabled"
    hide-edit-icon
    @cancel="onCancel"
    @focus="onFocus"
    @save="onSave"
  >
    <template #popupContent>
      <div class="flex flex-gap-1">
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId }"
          :input-id="`${id}-min`"
          :label="t('common.minParam', { param: t('common.amount') })"
        >
          <InputNumber
            ref="minInput"
            v-model="internalValue.min"
            v-bind="ariaProps"
            :input-id="inputId"
            :class="inputClasses"
            :currency="internalValue.currency ?? Currency.USD"
            :disabled="!!disabled"
            :placeholder="t('common.minParam', { param: t('common.amount') })"
            use-grouping
            mode="currency"
            @keyup.enter="onSave"
            @keyup.esc="closePopup"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId }"
          :input-id="`${id}-max`"
          :label="t('common.maxParam', { param: t('common.amount') })"
        >
          <InputNumber
            v-model="internalValue.max"
            v-bind="ariaProps"
            :input-id="inputId"
            :class="inputClasses"
            :currency="internalValue.currency ?? Currency.USD"
            :disabled="!!disabled"
            :placeholder="t('common.maxParam', { param: t('common.amount') })"
            use-grouping
            mode="currency"
            @keyup.enter="onSave"
            @keyup.esc="closePopup"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId }"
          :input-id="`${id}-currency`"
          :label="t('currency.currency')"
        >
          <CurrencyDropdown
            v-bind="ariaProps"
            v-model="internalValue.currency"
            :input-id="inputId"
            :class="inputClasses"
            show-clear
          />
        </InputWrapper>
      </div>
    </template>

    <template #placeholder="{ label: placeholderLabel }">
      <slot
        name="placeholder"
        :label="placeholderLabel"
        :placeholder="placeholderValue"
      >
        <InputGroup>
          <input
            :id="id"
            class="p-component p-inputtext w-full"
            :placeholder="placeholderLabel"
            :value="placeholderValue"
            @focus="onFocus"
          />

          <InputGroupAddon>
            <RygenButton
              :aria-label="
                t('common.clearParam', { param: t('search.filters.filters') })
              "
              icon="pi pi-times"
              @click.stop="onClear"
            />
          </InputGroupAddon>
        </InputGroup>
      </slot>
    </template>
  </PopupInputgroup>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import type { ComponentPublicInstance, PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { renderMoney } from '@rygen/rygen-ui/i18n';
import { Currency, Money, Optional } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import { MoneySearchFilter } from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import CurrencyDropdown from '@rygen/rygen-ui/components/form/CurrencyDropdown.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import PopupInputgroup from '@rygen/rygen-ui/components/popup/PopupInputgroup.vue';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputNumber from 'primevue/inputnumber';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Object as PropType<MoneySearchFilter>,
    default: () => new MoneySearchFilter(),
  },
  placeholder: {
    type: String,
    default: () => '',
  },
  appendTo: {
    type: String as PropType<'body' | 'self'>,
    default: () => 'self',
  },
  disabled: {
    type: [Boolean, String, Number, Array, Object],
    default: () => false,
  },
});
const { id, modelValue, placeholder, appendTo, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: MoneySearchFilter): void;
}>();

const { t } = useI18n();

const internalValue: Ref<MoneySearchFilter> = ref(
  new MoneySearchFilter(modelValue.value),
);
const popup: Ref<ComponentPublicInstance | null> = ref(null);
const minInput: Ref<ComponentPublicInstance | null> = ref(null);

const buildSinglePlaceholderValue = (
  amount: Optional<number>,
  currency: Optional<Currency>,
) => {
  const money = new Money({ amount, currency });
  return renderMoney(money, {
    defaultValue: '',
    suffix: currency ? ` ${currency}` : undefined,
  });
};

const placeholderValue = computed(() => {
  const { min, max, currency } = modelValue.value;
  if (min === undefined && max === undefined) {
    return currency;
  }

  if (min !== undefined && max === undefined) {
    const renderedMin = buildSinglePlaceholderValue(min, currency);
    return `> ${renderedMin}`;
  }

  return [min ?? 0, max]
    .filter(notNull)
    .map((amount) => buildSinglePlaceholderValue(amount, currency))
    .filter((s) => !!s)
    .join(' - ');
});

function onFocus() {
  if (disabled.value) {
    return;
  }

  nextTick(() => minInput.value?.$el.focus());
}

const onSave = async () => {
  if (!isEqual(internalValue.value, modelValue.value)) {
    emit('update:model-value', internalValue.value);
  }
};

const onCancel = () => {
  internalValue.value = new MoneySearchFilter(modelValue.value);
};

const closePopup = () => {
  if (!popup.value) {
    return;
  }

  const popupRef = popup.value as unknown as typeof PopupInputgroup;
  popupRef.onCancel();
};

const onClear = () => {
  internalValue.value = new MoneySearchFilter();
  if (!isEqual(internalValue.value, modelValue.value)) {
    emit('update:model-value', internalValue.value);
  }
};

watch(
  modelValue,
  (newVal) => {
    internalValue.value = new MoneySearchFilter(newVal);
  },
  { immediate: true },
);
</script>
